import { create } from 'zustand';
import type { AnswerRequest, BattleStatus, LeaderboardRow, MeResponse, StateSnapshot } from '@shared/types';
import type { Session } from '@/lib/session';
import { serverNow } from '@/lib/api';
import { dueAt, phoneSnapshot } from '@/lib/delay';

export type Connection = 'idle' | 'connecting' | 'live' | 'polling';
/** An answer as a format sends it (AnswerRequest without the index). */
export type AnswerPayload = Omit<AnswerRequest, 'index'>;
/**
 * What this phone locked in. `optionId` is the pick for single-pick formats (hotspot regions and rarity picks too);
 * number, order and multi 'all' keep their whole answer in `value`, `order` or `optionIds` (their `optionId` is empty
 * here, or the server's credit bucket when read back from /me). `elapsedMs` is the server's stamp once accepted.
 * `send` (9 Oct) is where its request stands; the lock is optimistic, so the question screen reads it to show the
 * answer as on its way, in, too late, or not sent and open to another tap. `undelivered` is set when the server stayed
 * too busy (429 or 5xx) to take the answer before the close, so the answer view can say what happened without blaming
 * the player's signal.
 */
export interface MyAnswer { index: number; optionId: string; lockedAt: number; send: SendState; elapsedMs?: number; value?: number; optionIds?: string[]; order?: string[]; undelivered?: Undelivered }
/**
 * Where a lock-in's request stands: `sending` on its way; `sent` accepted by the server (or already held there);
 * `late` refused as past the close, or the window shut while it was still trying; `failed` never got there (no answer
 * from the server, or no connection) while the window was open, so the options are free for another tap.
 */
export type SendState = 'sending' | 'sent' | 'late' | 'failed';
/** Why a locked-in answer never reached the server: it kept answering busy until the window shut. */
export type Undelivered = 'busy';
/** A leaderboard as this phone saw it at a reveal: the previous one gives rank movement and score deltas. */
export interface Board { index: number; top: LeaderboardRow[] }
export interface Toast { id: number; text: string }
/** How this phone came into the battle. A brand-new player who joins during a reveal goes straight to the leaderboard. */
export interface Joined { index: number; status: BattleStatus; rejoined: boolean }

export interface GameState {
  session?: Session; snapshot?: StateSnapshot; me?: MeResponse; meAt?: number; connection: Connection;
  myAnswer?: MyAnswer; error?: string;
  /** Server time when this phone first saw the reveal of the current question (the manual-mode answer view runs from it). */
  revealSeen?: { index: number; at: number };
  /** The last question index this phone saw open: if it locked nothing in, it did not answer. */
  seenQuestion?: number;
  /** The last two leaderboards seen, oldest first. */
  boards: Board[];
  joined?: Joined;
  toast?: Toast;
  /** This phone's clock when the server last confirmed an answer from it (9 Oct): an answer is a sign of life, so the heartbeat can skip a beat. */
  lastAnswerAt?: number;
  /** 9 Oct: the server said another device holds this seat now (lib/seat): the game page forgets the session and goes back to join. */
  seatMoved: boolean;
  /** 17 Oct 2026: the reveal whose leaderboard this phone has shown, by battle: a raised stream delay never takes it back to the answer view. */
  boardShownFor?: { code: string; index: number };
  setSession(s: Session | undefined): void; applySnapshot(s: StateSnapshot): void; setMe(m: MeResponse): void;
  setConnection(c: Connection): void; lockAnswer(index: number, answer: string | AnswerPayload): void;
  /**
   * The server took the lock-in for question `index` (with its stamp when it gave one): the answer is in. With `held`,
   * the answer the server says it holds replaces the local pick (a reply lost on the way, then a different tap): what
   * the phone shows is always what the server has.
   */
  confirmAnswer(index: number, elapsedMs?: number, held?: AnswerPayload): void;
  /** Where the lock-in's request stands now; an answer the server has taken is never marked again. Nothing happens for any other question. */
  markSend(index: number, send: Exclude<SendState, 'sent'>): void;
  /** The lock-in for question `index` never got through (lib/answer.ts gave up): it is late, and the answer view says why. */
  markUndelivered(index: number, why: Undelivered): void;
  markSeatMoved(): void;
  /** The page has acted on the seat moving (forgotten the session, gone back to join): the flag is cleared so it fires once. */
  clearSeatMoved(): void;
  /** The leaderboard for question `index` of the battle on screen is showing (the leaderboard screen, on mount). */
  markBoardShown(index: number): void;
  setError(e?: string): void; setJoined(j: Joined | undefined): void; showToast(text: string): void; clearToast(id?: number): void; reset(): void;
}
const initial = {
  session: undefined, snapshot: undefined, me: undefined, meAt: undefined, connection: 'idle' as Connection, myAnswer: undefined, error: undefined,
  revealSeen: undefined, seenQuestion: undefined, boards: [] as Board[], joined: undefined, toast: undefined, lastAnswerAt: undefined, seatMoved: false,
  boardShownFor: undefined,
};
let toastSeq = 0;

type SetGame = (partial: Partial<GameState>) => void;
type GetGame = () => GameState;

/**
 * 17 Oct 2026 (presenter console): the stream delay (lib/delay). Snapshots of the battle on screen whose moment is still
 * to come on this phone wait here, oldest first, each shown at its own moment: a reveal held while the next question
 * arrives still shows before it. A newer snapshot replaces a held one of the same moment, and any held one that would
 * land after it; an older one never replaces a newer one. All are dropped when the phone moves to another battle or resets.
 */
let pending: StateSnapshot[] = [];
let pendingTimer: ReturnType<typeof setTimeout> | undefined;
function dropPending() {
  pending = [];
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = undefined;
}
/**
 * 30 Oct 2026 (M2 fix round 2): true when this phone holds back a newer snapshot of battle `code` than `version`. The server
 * has moved on from that version, so the late fallback (screens/parts/lateTick) counts it as seen. Read only.
 */
export function holdsNewer(code: string, version: number): boolean {
  return pending.some((p) => p.code === code && p.version > version);
}
/** Arms the timer for the first held snapshot; once it is shown, the next one is armed. */
function armPending(apply: (s: StateSnapshot) => void) {
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = undefined;
  const first = pending[0]; if (!first) return;
  pendingTimer = setTimeout(() => { pendingTimer = undefined; const p = pending.shift(); if (p) apply(p); armPending(apply); }, Math.max(0, dueAt(first) - serverNow()));
}
function holdBack(s: StateSnapshot, apply: (s: StateSnapshot) => void) {
  const last = pending[pending.length - 1];
  if (last && last.code === s.code && s.version < last.version) return;
  const due = dueAt(s);
  pending = pending.filter((p) => p.code === s.code && !(p.status === s.status && p.index === s.index) && dueAt(p) < due);
  pending.push(s);
  armPending(apply);
}

/** Shows a snapshot now: versions only move forward within a battle, and another battle's snapshot starts afresh. */
function applyNow(set: SetGame, get: GetGame, s: StateSnapshot) {
  const st = get(); const cur = st.snapshot;
  // Versions only compare within one battle: a snapshot for another code starts afresh.
  const same = Boolean(cur && cur.code === s.code);
  if (cur && same) {
    if (s.version < cur.version) return;
    if (s.version === cur.version) {
      // Nothing is published when a player joins, so a fresher read of the same version only refreshes the live player count.
      if (s.at > cur.at) set({ snapshot: { ...cur, playerCount: s.playerCount, at: s.at } });
      return;
    }
  }
  const newQuestion = s.status === 'question' && (!same || cur!.index !== s.index);
  // The reveal snapshot may not repeat the question, so keep the one this phone was shown for the same index.
  const question = s.question ?? (s.status === 'reveal' && same && cur!.index === s.index ? cur!.question : undefined);
  const next: Partial<GameState> = { snapshot: question ? { ...s, question } : s };
  // A new question clears the last lock-in, unless it is this question's own, taken back from /me after a reload.
  if (newQuestion && st.myAnswer?.index !== s.index) next.myAnswer = undefined;
  if (cur && !same) Object.assign(next, { me: undefined, meAt: undefined, myAnswer: undefined, boards: [], revealSeen: undefined, seenQuestion: undefined, joined: undefined });
  if (s.status === 'question') next.seenQuestion = s.index;
  const seen = same ? st.revealSeen : undefined;
  if (s.status === 'reveal' && (!seen || seen.index !== s.index)) next.revealSeen = { index: s.index, at: serverNow() };
  if (s.status === 'reveal' && s.top) {
    const kept = (same ? st.boards : []).filter((b) => b.index < s.index);
    next.boards = [...kept, { index: s.index, top: s.top }].slice(-2);
  }
  set(next);
}

export const useGame = create<GameState>((set, get) => ({
  ...initial,
  setSession: (session) => set({ session }),
  // 17 Oct 2026: each moment shows on this phone at its server stamp plus the stream delay (lib/delay). A fresh join (the
  // first snapshot, or another battle's) drops anything held for the battle before; an older version than the one on
  // screen or held never replaces it. 30 Oct 2026 (m7): the fresh one waits for its moment too, so a phone that joins or
  // reloads during the delay never shows a reveal, an intro or the podium before the stream does (it shows the waiting
  // view meanwhile); a later snapshot of that battle, held or due, takes its place.
  applySnapshot: (raw) => {
    const s = phoneSnapshot(raw);
    const cur = get().snapshot;
    const fresh = !cur || cur.code !== s.code;
    // A battle not on screen yet whose snapshots are already held (it joined mid-delay) keeps them, in order.
    const joining = fresh && pending.length > 0 && pending[0].code === s.code;
    if (fresh && !joining) dropPending();
    // The same version or an older one has nothing to wait for: the player count refreshes, or it is refused.
    if (!fresh && s.version <= cur.version) { applyNow(set, get, s); return; }
    // Held for this battle only: a held snapshot never lands on another battle's screen. A battle not on screen yet is
    // the only one held (a fresh arrival dropped the rest), so its snapshots land whatever the screen shows.
    const apply = fresh ? (p: StateSnapshot) => applyNow(set, get, p) : (p: StateSnapshot) => { if (get().snapshot?.code === p.code) applyNow(set, get, p); };
    if (dueAt(s) > serverNow()) { holdBack(s, apply); return; }
    // Due now: what is held from before it goes; anything newer (publishes that crossed) still lands at its moment.
    if (pending.some((p) => p.version <= s.version)) { pending = pending.filter((p) => p.version > s.version); armPending(apply); }
    applyNow(set, get, s);
  },
  setMe: (me) => {
    const restored = restoredAnswer(me, get());
    set(restored ? { me, meAt: Date.now(), myAnswer: restored } : { me, meAt: Date.now() });
  },
  setConnection: (connection) => set({ connection }),
  lockAnswer: (index, answer) => set({ myAnswer: { index, lockedAt: serverNow(), send: 'sending', ...answerFields(typeof answer === 'string' ? { optionId: answer } : answer) } }),
  confirmAnswer: (index, elapsedMs, held) => {
    const st = get(); const a = st.myAnswer; if (!a || a.index !== index) return;
    const { undelivered: _gone, optionId: _mine, value: _v, optionIds: _ids, order: _o, ...rest } = a;
    const fields = held ? answerFields(held) : answerFields(a);
    // The server's answer was locked when its stamp says, counted from the question's start when this phone knows it.
    const startsAt = held && st.snapshot?.code === st.session?.code && st.snapshot?.index === index ? st.snapshot.question?.startsAt : undefined;
    const lockedAt = held && typeof elapsedMs === 'number' && startsAt !== undefined ? startsAt + elapsedMs : a.lockedAt;
    set({ myAnswer: { ...rest, ...fields, lockedAt, send: 'sent', ...(typeof elapsedMs === 'number' ? { elapsedMs } : {}) }, lastAnswerAt: Date.now() });
  },
  markSend: (index, send) => { const a = get().myAnswer; if (a && a.index === index && a.send !== 'sent') set({ myAnswer: { ...a, send } }); },
  markUndelivered: (index, why) => { const a = get().myAnswer; if (a && a.index === index && a.send !== 'sent') set({ myAnswer: { ...a, send: 'late', undelivered: why } }); },
  markSeatMoved: () => set({ seatMoved: true }),
  clearSeatMoved: () => set({ seatMoved: false }),
  markBoardShown: (index) => {
    const code = get().snapshot?.code; if (code === undefined) return;
    const b = get().boardShownFor; if (b && b.code === code && b.index === index) return;
    set({ boardShownFor: { code, index } });
  },
  setError: (error) => set({ error }),
  setJoined: (joined) => set({ joined }),
  showToast: (text) => set({ toast: { id: ++toastSeq, text } }),
  clearToast: (id) => { const t = get().toast; if (t && (id === undefined || t.id === id)) set({ toast: undefined }); },
  reset: () => { dropPending(); set({ ...initial, boards: [] }); },
}));

/** The answer fields of a lock-in, copied so the store never shares arrays with the caller. */
function answerFields(a: Partial<Pick<MyAnswer, 'optionId' | 'value' | 'optionIds' | 'order'>>): Pick<MyAnswer, 'optionId' | 'value' | 'optionIds' | 'order'> {
  const out: Pick<MyAnswer, 'optionId' | 'value' | 'optionIds' | 'order'> = { optionId: typeof a.optionId === 'string' ? a.optionId : '' };
  if (typeof a.value === 'number' && Number.isFinite(a.value)) out.value = a.value;
  if (Array.isArray(a.optionIds)) out.optionIds = [...a.optionIds];
  if (Array.isArray(a.order)) out.order = [...a.order];
  return out;
}

/**
 * After a reload mid-question the server still holds what this player locked in (GET /me returns the open question's
 * choice and time, without the verdict). The phone takes it back, so it shows the answer locked in instead of asking again.
 * Number, order and multi 'all' records carry their value, order or picks; any of them counts.
 */
export function restoredAnswer(me: MeResponse, st: Pick<GameState, 'session' | 'snapshot' | 'myAnswer'>): MyAnswer | undefined {
  const s = me.state; if (!s || s.status !== 'question') return undefined;
  if (st.session && st.session.playerId !== me.playerId) return undefined;
  if (st.myAnswer?.index === s.index) return undefined;
  const rec = me.answers?.[`q${s.index}`]; if (!rec) return undefined;
  const fields = answerFields(rec);
  const held = fields.optionId !== '' || fields.value !== undefined || Boolean(fields.optionIds?.length) || Boolean(fields.order?.length);
  if (!held) return undefined;
  const startsAt = s.question?.startsAt ?? (st.snapshot?.code === s.code && st.snapshot.index === s.index ? st.snapshot.question?.startsAt : undefined);
  const elapsedMs = Math.max(0, rec.elapsedMs ?? 0);
  // The server holds it, so it is in.
  return { index: s.index, ...fields, lockedAt: (startsAt ?? serverNow()) + elapsedMs, send: 'sent', elapsedMs };
}

export type Phase = 'join' | 'lobby' | 'preroll' | 'question' | 'reveal' | 'podium' | 'ended';
export function selectPhase(s: Pick<GameState, 'session' | 'snapshot'>): Phase {
  if (!s.session || !s.snapshot) return 'join';
  const snap = s.snapshot;
  if (snap.status === 'lobby') return 'lobby';
  // 17 Oct 2026: a delayed question shows its intro from the stub (`upcoming`) until its text arrives, even past its start.
  if (snap.status === 'question') { const q = snap.question ?? snap.upcoming; return !snap.question || (q && serverNow() < q.startsAt) ? 'preroll' : 'question'; }
  if (snap.status === 'reveal') return 'reveal';
  if (snap.status === 'podium') return 'podium';
  return 'ended';
}

export const DEFAULT_REVEAL_MS = 7000;
export const DEFAULT_BOARD_MS = 6000;

/**
 * Server time at which the answer view gives way to the leaderboard. On autopilot every phone switches together,
 * `boardMs` before the next question opens; in manual mode the answer view runs `revealMs` from when this phone saw the reveal.
 */
export function boardAt(snap: StateSnapshot, seenAt?: number): number | undefined {
  if (snap.status !== 'reveal') return undefined;
  const revealMs = snap.revealMs ?? DEFAULT_REVEAL_MS; const boardMs = snap.boardMs ?? DEFAULT_BOARD_MS;
  if (snap.mode === 'autopilot' && typeof snap.revealUntil === 'number') return snap.revealUntil - boardMs;
  return (seenAt ?? snap.at) + revealMs;
}

export type RevealView = 'answer' | 'board';
export function selectRevealView(s: Pick<GameState, 'snapshot' | 'revealSeen' | 'joined' | 'boardShownFor'>, now: number): RevealView {
  const snap = s.snapshot; if (!snap || snap.status !== 'reveal') return 'answer';
  // 17 Oct 2026: never back from a leaderboard this phone has shown (a raised delay moves the end of the reveal later).
  const b = s.boardShownFor; if (b && b.code === snap.code && b.index === snap.index) return 'board';
  // A new player who arrives during this reveal never saw the question, so they go straight to the leaderboard.
  const j = s.joined; if (j && !j.rejoined && j.status === 'reveal' && j.index === snap.index) return 'board';
  const at = boardAt(snap, s.revealSeen?.index === snap.index ? s.revealSeen.at : undefined);
  return at !== undefined && now >= at ? 'board' : 'answer';
}
